import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, FileText, Users, TrendingUp, Globe, BookOpen, CheckCircle, Briefcase, Calculator, PieChart, DollarSign } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const Services = () => {
  const foodServices = [
    {
      icon: Briefcase,
      title: 'Food Machinery Solutions',
      description: 'We connect businesses with reliable food machinery solutions',
      details: [
        'Machinery selection and sourcing',
        'Supplier coordination',
        'Production line improvement',
        'Equipment solutions based on business needs'
      ]
    },
    {
      icon: Award,
      title: 'Ingredient Solutions',
      description: 'Ingredient sourcing and technical support',
      details: [
        'Ingredient selection',
        'Functional ingredient solutions',
        'Product improvement',
        'Supplier connections',
        'Innovation support'
      ]
    },
    {
      icon: Shield,
      title: 'Food Safety Management Systems & Auditing',
      description: 'Comprehensive support for globally recognized food safety systems',
      details: [
        'FSSC 22000 Implementation & Certification Support',
        'ISO 22000 Food Safety Management Systems',
        'BRCGS Standards Compliance',
        'IFS Standards Implementation',
        'Internal audits and gap assessments',
        'Certification preparation and continuous improvement support'
      ]
    },
    {
      icon: FileText,
      title: 'HACCP Implementation',
      description: 'Expert HACCP specialists helping businesses establish effective hazard control systems',
      details: [
        'Developing HACCP plans',
        'Conducting hazard analysis',
        'Identifying critical control points (CCPs)',
        'Implementing monitoring procedures',
        'Supporting verification and validation activities'
      ]
    },
    {
      icon: Users,
      title: 'Supplier Audits',
      description: 'Ensuring your supply chain meets quality and safety expectations',
      details: [
        'Supplier qualification programs',
        'Supplier risk assessments',
        'On-site supplier audits',
        'Quality performance evaluations',
        'Corrective action follow-up'
      ]
    },
    {
      icon: BookOpen,
      title: 'Regulatory Compliance',
      description: 'Navigate complex food regulations with expert guidance',
      details: [
        'European food legislation',
        'International food safety requirements',
        'Product and process compliance',
        'Documentation and regulatory reviews'
      ]
    },
    {
      icon: Award,
      title: 'Label Compliance',
      description: 'Professional label review services for legal compliance',
      details: [
        'Ingredient declaration review',
        'Allergen compliance',
        'Nutritional information verification',
        'Claims and labeling requirements',
        'Market-specific label requirements'
      ]
    },
    {
      icon: BookOpen,
      title: 'Food Safety Training & Coaching',
      description: 'Empower your team with practical knowledge through customized training',
      details: [
        'HACCP principles',
        'Food hygiene practices',
        'Food safety culture',
        'Internal auditing',
        'Quality management systems',
        'Employee coaching and mentoring'
      ]
    }
  ];

  const financeServices = [
    {
      icon: DollarSign,
      title: 'Passive Income Planning',
      description: 'Sustainable passive income strategies for individuals',
      details: [
        'Financial planning',
        'Income diversification',
        'Investment awareness',
        'Goal-based wealth planning'
      ]
    },
    {
      icon: Globe,
      title: 'Company Incorporation',
      description: 'Business setup in strategic international markets',
      details: [
        'Company registration in The Netherlands',
        'Company registration in Dubai',
        'Business structure guidance',
        'Documentation assistance',
        'Start-up advisory services'
      ]
    },
    {
      icon: Calculator,
      title: 'Tax & VAT Filing',
      description: 'Stay compliant with Dutch tax requirements',
      details: [
        'VAT registration and filing support',
        'Tax reporting assistance',
        'Compliance guidance',
        'Financial documentation support'
      ]
    },
    {
      icon: PieChart,
      title: 'Bookkeeping & Financial Auditing',
      description: 'Professional financial management support',
      details: [
        'Daily bookkeeping services',
        'Financial reporting',
        'Corporate Income Tax (CIT) support',
        'Financial audit preparation',
        'Business performance analysis'
      ]
    },
    {
      icon: TrendingUp,
      title: 'Long-Term Investment Planning',
      description: 'Structured investment strategies for wealth building',
      details: [
        'Wealth building strategies',
        'Financial security planning',
        'Risk management',
        'Long-term financial objectives'
      ]
    }
  ];

  return (
    <div className="services-page" style={{ backgroundColor: 'var(--color-cream)' }}>
      <SEO
        title="Our Services"
        path="/services"
        description="Food consultancy services: FSSC 22000, ISO 22000, HACCP implementation, supplier audits, label & regulatory compliance, food machinery & ingredient solutions. Finance services: company incorporation, tax & VAT filing, bookkeeping, investment & passive income planning."
        keywords="food safety management, HACCP implementation, FSSC 22000, supplier audits, label compliance, food machinery, ingredient solutions, company incorporation Netherlands Dubai, tax VAT filing, bookkeeping, passive income planning"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20" style={{ backgroundColor: 'var(--color-navy)' }}>
        <div className="container">
          <AnimatedSection>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="font-serif text-6xl md:text-7xl font-bold mb-6" style={{ color: 'var(--color-gold)' }}>
                Our Services
              </h1>
              <p className="text-xl md:text-2xl leading-relaxed" style={{ color: 'var(--color-cream)', opacity: 0.9 }}>
                Expert solutions that help businesses achieve excellence in food safety, regulatory compliance, operational efficiency, and financial growth.
              </p>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* Food Consultancy Section */}
      <section id="food" className="section">
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-16">
              <motion.div
                whileInView={{ scale: [0.8, 1.05, 1] }}
                transition={{ duration: 0.6 }}
                className="inline-block mb-4 px-4 py-2 rounded-full"
                style={{ backgroundColor: 'var(--color-emerald)', color: 'white' }}
              >
                <span className="text-sm font-semibold tracking-wide">FOOD INDUSTRY</span>
              </motion.div>
              <h2 className="font-serif text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-navy)' }}>
                Food Consultancy
              </h2>
              <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--color-dark-gray)' }}>
                We help food businesses build strong foundations through internationally recognized food safety systems, compliance solutions, and strategic guidance.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8">
            {foodServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -10, boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }}
                    className="bg-white p-8 h-full border-t-4"
                    style={{ borderTopColor: 'var(--color-emerald)' }}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-3 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--color-emerald)' }}>
                        <Icon size={28} style={{ color: 'white' }} />
                      </div>
                      <div>
                        <h3 className="font-serif text-2xl font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>
                          {service.title}
                        </h3>
                        <p className="text-base mb-4" style={{ color: 'var(--color-gray)' }}>
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {service.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle size={18} className="mt-1 flex-shrink-0" style={{ color: 'var(--color-emerald)' }} />
                          <span className="text-sm" style={{ color: 'var(--color-dark-gray)' }}>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Finance Consultancy Section */}
      <section id="finance" className="section">
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-16">
              <motion.div
                whileInView={{ scale: [0.8, 1.05, 1] }}
                transition={{ duration: 0.6 }}
                className="inline-block mb-4 px-4 py-2 rounded-full"
                style={{ backgroundColor: 'var(--color-gold)', color: 'var(--color-navy)' }}
              >
                <span className="text-sm font-semibold tracking-wide">FINANCIAL SERVICES</span>
              </motion.div>
              <h2 className="font-serif text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-navy)' }}>
                Finance Consultancy
              </h2>
              <p className="text-xl max-w-3xl mx-auto" style={{ color: 'var(--color-dark-gray)' }}>
                We support entrepreneurs and businesses with financial solutions designed to simplify operations, improve financial planning, and create long-term growth opportunities.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {financeServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -10, scale: 1.02 }}
                    className="bg-white p-8 h-full border-l-4 shadow-lg"
                    style={{ borderLeftColor: 'var(--color-gold)' }}
                  >
                    <div className="p-3 rounded-full inline-block mb-4" style={{ backgroundColor: 'var(--color-gold)' }}>
                      <Icon size={28} style={{ color: 'white' }} />
                    </div>
                    <h3 className="font-serif text-2xl font-semibold mb-3" style={{ color: 'var(--color-navy)' }}>
                      {service.title}
                    </h3>
                    <p className="text-base mb-4" style={{ color: 'var(--color-gray)' }}>
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.details.map((detail, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle size={18} className="mt-1 flex-shrink-0" style={{ color: 'var(--color-gold)' }} />
                          <span className="text-sm" style={{ color: 'var(--color-dark-gray)' }}>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <AnimatedSection>
        <section className="py-20" style={{ backgroundColor: 'var(--color-emerald)' }}>
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="font-serif text-5xl font-bold mb-6 text-white">
                Why Choose Prime Food & Consultancy?
              </h2>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
              {[
                'Industry-focused expertise',
                'International standards knowledge',
                'Practical business solutions',
                'Customized consulting approach',
                'Support for startups & SMEs',
                'Trusted partner commitment'
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm p-6 text-center"
                >
                  <CheckCircle size={32} className="mx-auto mb-3" style={{ color: 'var(--color-gold)' }} />
                  <p className="font-medium text-white">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection>
        <section className="py-24" style={{ backgroundColor: 'var(--color-navy)' }}>
          <div className="container text-center">
            <motion.h2
              whileInView={{ scale: [0.9, 1] }}
              className="font-serif text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-gold)' }}
            >
              Ready to Get Started?
            </motion.h2>
            <motion.p
              whileInView={{ opacity: [0, 1] }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-10 max-w-2xl mx-auto" style={{ color: 'var(--color-cream)', opacity: 0.9 }}
            >
              Let's discuss how we can help your business achieve excellence and sustainable growth.
            </motion.p>
            <motion.div
              whileInView={{ y: [30, 0], opacity: [0, 1] }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                >
                  <span>Schedule Consultation</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Services;
